import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
// `CartErrorEvent` carries no static eventName, so it is listened for by its ThemeEvents constant.
import { CartAddEvent, CartAddPendingEvent, ThemeEvents } from '@theme/events';
import { isMobileBreakpoint, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';

/** Movement before we decide whether the finger meant "scroll the cart" or "put the drawer away". */
const DRAG_AXIS_THRESHOLD = 8;

/** How far across the panel the drag has to carry for release to mean close. */
const DRAG_CLOSE_FRACTION = 0.3;

const DRAGGING_CLASS = 'cart-drawer__dialog--dragging';
const RELEASING_CLASS = 'cart-drawer__dialog--releasing';

/**
 * Whether anything between `node` and `boundary` scrolls sideways and so owns horizontal gestures
 * itself — the cross-sell strip in the drawer does (`scroll-snap-type: x mandatory`).
 *
 * @param {EventTarget | null} node
 * @param {Element} boundary
 * @returns {boolean}
 */
function ownsHorizontalGesture(node, boundary) {
  for (let element = node; element instanceof Element && element !== boundary; element = element.parentElement) {
    if (element.scrollWidth > element.clientWidth + 1) return true;
  }

  return false;
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  /** Whether this drawer is open only because an add-to-cart request is in flight. Such a drawer
   *  has to be taken back if the add fails; one the shopper opened themselves must not be.
   *  @type {boolean} */
  #openedOptimistically = false;

  /** The in-flight dismiss gesture. @type {{pointerId: number, startX: number, startY: number, offset: number, axis: 'x'|'y'|null} | null} */
  #drag = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('pointerdown', this.#handleDragStart);
    // Pointer events cannot call off a scroll; this one exists purely to do that once the gesture
    // is committed to the horizontal. Deliberately not passive.
    this.addEventListener('touchmove', this.#holdOffScrolling, { passive: false });
    document.addEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    document.addEventListener(ThemeEvents.cartError, this.#handleCartError);
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#handleDragStart);
    this.removeEventListener('touchmove', this.#holdOffScrolling);
    this.#endDrag();
    document.removeEventListener(CartAddPendingEvent.eventName, this.#handleCartAddPending);
    document.removeEventListener(ThemeEvents.cartError, this.#handleCartError);
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Drag to dismiss. The drawer is a modal dialog in the top layer, where a phone will not start
   * its own edge-swipe — the back gesture never reaches the page, so the panel carries its own.
   * Rightwards only, the direction it already leaves in.
   *
   * @param {PointerEvent} event
   */
  #handleDragStart = (event) => {
    const { dialog } = this.refs;

    if (event.pointerType !== 'touch' || !dialog?.open || this.#drag) return;
    // Something inside wants this gesture for itself — the cross-sell strip scrolls sideways.
    if (ownsHorizontalGesture(event.target, dialog)) return;

    this.#drag = { pointerId: event.pointerId, startX: event.clientX, startY: event.clientY, offset: 0, axis: null };

    this.addEventListener('pointermove', this.#handleDragMove);
    this.addEventListener('pointerup', this.#handleDragEnd);
    this.addEventListener('pointercancel', this.#handleDragCancel);
  };

  /** @param {PointerEvent} event */
  #handleDragMove = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const dx = event.clientX - drag.startX;
    const dy = event.clientY - drag.startY;

    if (!drag.axis) {
      if (Math.abs(dx) < DRAG_AXIS_THRESHOLD && Math.abs(dy) < DRAG_AXIS_THRESHOLD) return;

      // A finger that set off downwards is reading the cart, not dismissing it.
      drag.axis = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (drag.axis === 'y') {
        this.#endDrag();
        return;
      }

      this.refs.dialog.classList.add(DRAGGING_CLASS);
    }

    drag.offset = Math.max(0, dx);
    this.refs.dialog.style.setProperty('--cart-drawer-drag', `${drag.offset}px`);
  };

  /** @param {PointerEvent} event */
  #handleDragEnd = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    const committed = drag.axis === 'x';
    const width = this.refs.dialog.getBoundingClientRect().width || window.innerWidth;
    const dismiss = committed && drag.offset > width * DRAG_CLOSE_FRACTION;

    this.#endDrag({ keepOffset: committed });

    if (committed) this.#settleDrag(dismiss);
  };

  /** @param {PointerEvent} event */
  #handleDragCancel = (event) => {
    const drag = this.#drag;
    if (!drag || event.pointerId !== drag.pointerId) return;

    // An interrupted gesture is not a decision to close.
    const committed = drag.axis === 'x';
    this.#endDrag({ keepOffset: committed });
    if (committed) this.#settleDrag(false);
  };

  /** Stops tracking. Leaves the panel where the finger left it when a settle animation follows. */
  #endDrag({ keepOffset = false } = {}) {
    this.removeEventListener('pointermove', this.#handleDragMove);
    this.removeEventListener('pointerup', this.#handleDragEnd);
    this.removeEventListener('pointercancel', this.#handleDragCancel);

    this.#drag = null;

    const { dialog } = this.refs;
    if (!dialog) return;

    dialog.classList.remove(DRAGGING_CLASS);
    if (!keepOffset) dialog.style.removeProperty('--cart-drawer-drag');
  }

  /**
   * Animates the panel the rest of the way — out of the viewport, or back to where it started.
   * @param {boolean} dismiss
   */
  async #settleDrag(dismiss) {
    const { dialog } = this.refs;
    const target = dismiss ? '100%' : '0px';

    if (prefersReducedMotion()) {
      dialog.style.setProperty('--cart-drawer-drag', target);
    } else {
      dialog.classList.add(RELEASING_CLASS);
      // Flush the swap out of `--dragging` (which suppresses transitions) so the value change below
      // is animated from where the finger left the panel rather than applied outright.
      void dialog.offsetWidth;
      dialog.style.setProperty('--cart-drawer-drag', target);
      await onAnimationEnd(dialog, undefined, { subtree: false });
    }

    if (dismiss) {
      // The panel is already off-screen; replaying the drawer's own closing animation would drag it
      // back into view first.
      dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }

    dialog.classList.remove(RELEASING_CLASS);
    dialog.style.removeProperty('--cart-drawer-drag');
  }

  /** @param {TouchEvent} event */
  #holdOffScrolling = (event) => {
    if (this.#drag?.axis === 'x' && event.cancelable) event.preventDefault();
  };

  /**
   * Opens the drawer as soon as an add-to-cart request goes out, so its animation runs alongside
   * the request instead of after it. The contents are still the pre-add cart until `#handleCartAdd`
   * morphs them in — `cart-pending` is there for styling that gap.
   */
  #handleCartAddPending = () => {
    if (!this.hasAttribute('auto-open') || this.refs.dialog?.open) return;

    this.#openedOptimistically = true;
    this.setAttribute('cart-pending', '');
    this.showDialog();
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number }, data?: { didError?: boolean } }>} event
   */
  #handleCartAdd = (event) => {
    this.removeAttribute('cart-pending');

    // The theme dispatches a `didError` update after a rejected add so the cart icon can resync.
    // That is not an add, and a drawer must not present it as one.
    if (event.detail.data?.didError) {
      this.#withdrawOptimisticOpen();
      return;
    }

    this.#openedOptimistically = false;

    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /** The add was refused (sold out, cart limit); undo the open it was predicated on. */
  #handleCartError = () => {
    this.removeAttribute('cart-pending');
    this.#withdrawOptimisticOpen();
  };

  /** Closes the drawer only if this component is the one that opened it optimistically. */
  #withdrawOptimisticOpen() {
    if (!this.#openedOptimistically) return;

    this.#openedOptimistically = false;

    // `showDialog` defers the actual `showModal()` to the next animation frame, and a rejected add
    // can come back before that frame runs — `closeDialog` would then find a dialog that is not
    // open yet, do nothing, and the drawer would open a moment later with nothing added. Queueing
    // the close behind a frame of our own puts it after the open it is undoing.
    requestAnimationFrame(() => this.closeDialog());
  }

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
